/**
 * <kik-product-card> — makes the colour swatches on a KIK product card
 * interactive. Picking one swaps the card's image to that variant's own photo and
 * repoints the card's links at `?variant=<id>`, so clicking through opens the
 * colour the customer chose.
 *
 * The hover image stacked over the primary is deliberately left alone: it is the
 * product's shared 3rd photo rather than any one colour's, so it keeps revealing
 * on hover after a swatch is picked.
 *
 * Progressive enhancement: with this script blocked the swatches are inert
 * buttons and every link on the card still works.
 *
 * Rendered by snippets/kik-product-card.liquid. Hooks: [data-kik-swatch] and
 * [data-kik-card-link] are data attributes; the primary image is matched by class
 * because it comes out of Liquid's `image_tag` filter, whose named arguments
 * cannot contain hyphens — so `data-kik-card-image:` is not expressible there.
 */
class KikProductCard extends HTMLElement {
  connectedCallback() {
    this.swatches = Array.from(this.querySelectorAll('[data-kik-swatch]'));
    if (!this.swatches.length) return;

    this.image = this.querySelector(
      '.kik-product-card__image:not(.kik-product-card__image--secondary)'
    );
    this.links = Array.from(this.querySelectorAll('[data-kik-card-link]'));

    // Remembered so a colour with no photo of its own can restore the product's
    // featured image rather than leaving the previous colour's photo on screen.
    this.defaultSrc = this.image ? this.image.getAttribute('src') : null;
    this.defaultSrcset = this.image ? this.image.getAttribute('srcset') : null;
    this.defaultHrefs = this.links.map((link) => link.getAttribute('href'));

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    if (this.onClick) this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    const swatch = event.target.closest('[data-kik-swatch]');
    if (!swatch || !this.contains(swatch)) return;

    this.select(swatch);
  }

  select(swatch) {
    for (const item of this.swatches) {
      const isCurrent = item === swatch;
      item.setAttribute('aria-pressed', String(isCurrent));
      item.classList.toggle('kik-product-card__swatch--current', isCurrent);
    }

    this.setImage(swatch.dataset.kikVariantImage);
    this.setLinks(swatch.dataset.kikVariantUrl);
  }

  setImage(src) {
    if (!this.image) return;

    const next = src || this.defaultSrc;
    if (!next) return;

    this.image.setAttribute('src', next);

    // srcset would keep winning over the new src, so it goes while a variant
    // photo is showing. That photo is requested at one generous width (see the
    // snippet) instead of a full candidate set — a deliberate trade for not
    // hand-building srcset strings in Liquid.
    if (src) {
      this.image.removeAttribute('srcset');
    } else if (this.defaultSrcset) {
      this.image.setAttribute('srcset', this.defaultSrcset);
    }
  }

  setLinks(url) {
    for (const [index, link] of this.links.entries()) {
      link.setAttribute('href', url || this.defaultHrefs[index]);
    }
  }
}

if (!customElements.get('kik-product-card')) {
  customElements.define('kik-product-card', KikProductCard);
}
